import { useRef, useEffect, useState } from 'react';

type Props = {
  mode: 'draw' | 'upload';
  value: string | null;
  onChange: (v: string | null) => void;
};

/**
 * 电子签字段：mode=draw 用 canvas 手写；mode=upload 用文件选择
 * 输出：base64 字符串
 */
export default function SignaturePad({ mode, value, onChange }: Props) {
  if (mode === 'upload') return <UploadMode value={value} onChange={onChange} />;
  return <DrawMode value={value} onChange={onChange} />;
}

/**
 * 手写模式：内嵌处只做预览/入口，点击进入全屏签名板（手机端可全屏书写，防误触滚动）。
 */
function DrawMode({ value, onChange }: { value: string | null; onChange: (v: string | null) => void }) {
  const [open, setOpen] = useState(false);

  return (
    <div className="sig-pad">
      {value ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <img
            src={value}
            alt="签名预览"
            style={{ width: 220, maxWidth: '60vw', background: '#fff', border: '1px solid var(--border-strong)', borderRadius: 8 }}
          />
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" className="btn" onClick={() => setOpen(true)}>重新签名</button>
            <button type="button" className="btn" onClick={() => onChange(null)}>清空</button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          className="btn"
          onClick={() => setOpen(true)}
          style={{
            width: '100%', maxWidth: 480, minHeight: 96, borderStyle: 'dashed',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, fontSize: 15,
          }}
        >
          ✍ 点击进入全屏签名
        </button>
      )}
      {open && (
        <FullscreenSign
          onCancel={() => setOpen(false)}
          onConfirm={(dataUrl) => {
            onChange(dataUrl);
            setOpen(false);
          }}
        />
      )}
    </div>
  );
}

/** 全屏签名板：铺满视口、锁定页面滚动，适配手机高分屏（DPR 缩放） */
function FullscreenSign({ onCancel, onConfirm }: { onCancel: () => void; onConfirm: (dataUrl: string) => void }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const drawingRef = useRef(false);
  const [hasInk, setHasInk] = useState(false);

  useEffect(() => {
    // 锁定背景滚动
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const canvas = canvasRef.current;
    if (canvas) {
      const rect = canvas.getBoundingClientRect();
      const dpr = Math.min(3, window.devicePixelRatio || 1);
      canvas.width = Math.round(rect.width * dpr);
      canvas.height = Math.round(rect.height * dpr);
      const ctx = canvas.getContext('2d')!;
      ctx.scale(dpr, dpr);
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, rect.width, rect.height);
      ctx.strokeStyle = '#1f2937';
      ctx.lineWidth = 3;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
    }
    return () => { document.body.style.overflow = prev; };
  }, []);

  const pos = (e: React.PointerEvent) => {
    const rect = canvasRef.current!.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };
  const start = (e: React.PointerEvent) => {
    drawingRef.current = true;
    canvasRef.current!.setPointerCapture?.(e.pointerId);
    const ctx = canvasRef.current!.getContext('2d')!;
    const { x, y } = pos(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.lineTo(x + 0.1, y + 0.1); // 点按也留痕
    ctx.stroke();
    setHasInk(true);
  };
  const move = (e: React.PointerEvent) => {
    if (!drawingRef.current) return;
    const ctx = canvasRef.current!.getContext('2d')!;
    const { x, y } = pos(e);
    ctx.lineTo(x, y);
    ctx.stroke();
  };
  const end = () => { drawingRef.current = false; };
  const clear = () => {
    const canvas = canvasRef.current!;
    const rect = canvas.getBoundingClientRect();
    const ctx = canvas.getContext('2d')!;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, rect.width, rect.height);
    setHasInk(false);
  };

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 1000, background: '#fff',
        display: 'flex', flexDirection: 'column',
      }}
      role="dialog"
      aria-label="全屏签名"
    >
      <div style={{ padding: '10px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #e5e7eb' }}>
        <strong style={{ fontSize: 15, color: '#1f2937' }}>请在下方空白区域签名</strong>
        <button type="button" className="btn" onClick={onCancel}>✕ 关闭</button>
      </div>
      <div style={{ flex: 1, position: 'relative', background: '#f4f5f7', padding: 10 }}>
        <canvas
          ref={canvasRef}
          onPointerDown={start}
          onPointerMove={move}
          onPointerUp={end}
          onPointerCancel={end}
          style={{
            touchAction: 'none', width: '100%', height: '100%', display: 'block',
            background: '#fff', border: '1px dashed #cbd2dd', borderRadius: 10, cursor: 'crosshair',
          }}
        />
        {!hasInk && (
          <span style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#c2c8d4', fontSize: 22, pointerEvents: 'none', letterSpacing: 4 }}>
            签 名 区
          </span>
        )}
      </div>
      <div style={{ padding: '10px 16px calc(10px + env(safe-area-inset-bottom))', display: 'flex', gap: 10, justifyContent: 'flex-end', borderTop: '1px solid #e5e7eb' }}>
        <button type="button" className="btn" onClick={clear} disabled={!hasInk}>清空重写</button>
        <button type="button" className="btn" onClick={onCancel}>取消</button>
        <button
          type="button"
          className="btn btn-primary"
          disabled={!hasInk}
          onClick={() => onConfirm(canvasRef.current!.toDataURL('image/png'))}
        >
          确认签名
        </button>
      </div>
    </div>
  );
}

function UploadMode({ value, onChange }: { value: string | null; onChange: (v: string | null) => void }) {
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFile = (file: File) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      alert('图片不能超过 10MB');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result as string;
      // 小图直接用；大图等比缩到 1024px 内并转 JPEG，避免 base64 撑爆请求体
      if (file.size <= 300 * 1024) {
        onChange(dataUrl);
        return;
      }
      const img = new Image();
      img.onload = () => {
        const max = 1024;
        const scale = Math.min(1, max / Math.max(img.width, img.height));
        const canvas = document.createElement('canvas');
        canvas.width = Math.max(1, Math.round(img.width * scale));
        canvas.height = Math.max(1, Math.round(img.height * scale));
        const ctx = canvas.getContext('2d')!;
        ctx.fillStyle = '#ffffff'; // PNG 透明底转 JPEG 需铺白
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        onChange(canvas.toDataURL('image/jpeg', 0.85));
      };
      img.onerror = () => alert('图片读取失败，请换一张试试');
      img.src = dataUrl;
    };
    reader.readAsDataURL(file);
  };

  return (
    <div className="sig-upload">
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg"
        style={{ display: 'none' }}
        onChange={(e) => e.target.files?.[0] && handleFile(e.target.files[0])}
      />
      <button type="button" className="btn" onClick={() => inputRef.current?.click()}>
        选择签名图片
      </button>
      {value && (
        <div className="sig-preview">
          <img src={value} alt="签名" style={{ maxWidth: 320, border: '1px solid var(--border)' }} />
          <button type="button" className="icon-btn icon-danger" onClick={() => onChange(null)} title="删除">
            ✕
          </button>
        </div>
      )}
    </div>
  );
}
